import React, { useRef, useState, useMemo } from "react";
import { Button, Row } from "reactstrap";
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import setCanvasPreview from "./setCanvasPreview";

/**
 * Props:
 *  - updateAvatar: (dataUrl: string, blob: Blob|null) => void   [required]
 *  - src?: string            // dataURL or URL from parent; if omitted, shows its own file input
 *  - aspect?: number         // default 1 (square)
 *  - minDimension?: number   // default 150
 *  - accept?: string         // file input accept; default "image/*"
 *  - className?: string
 */
const CircularImageCropper = ({
  updateAvatar,
  src,
  aspect = 1,
  minDimension = 150,
  accept = "image/*",
  className = "",
}) => {
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);

  // If parent doesn’t pass src, we allow picking locally
  const [localSrc, setLocalSrc] = useState("");
  const effectiveSrc = src ?? localSrc;

  const [crop, setCrop] = useState();
  const [error, setError] = useState("");

  // ---- Utils ----
  const canvasToBlob = (canvas, type = "image/png", quality = 0.92) =>
    new Promise((resolve, reject) => {
      try {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error("canvas.toBlob() returned null"));
        }, type, quality);
      } catch (e) {
        reject(e);
      }
    });

  const readFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.onload = () => resolve(String(reader.result || ""));
      reader.readAsDataURL(file);
    });

  const ensureMinDimensions = (dataUrl) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (
          img.naturalWidth >= minDimension &&
          img.naturalHeight >= minDimension
        ) {
          resolve();
        } else {
          reject(
            new Error(`Image must be at least ${minDimension} x ${minDimension} pixels.`)
          );
        }
      };
      img.onerror = () => reject(new Error("Unable to load image"));
      img.src = dataUrl;
    });

  // ---- Handlers ----
  const onSelectFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await readFileAsDataURL(file);
      await ensureMinDimensions(dataUrl);
      if (error) setError("");
      setLocalSrc(dataUrl);
    } catch (err) {
      setError(err?.message || "Invalid image selected.");
      setLocalSrc("");
    }
  };

  const onImageLoad = (e) => {
    const img = e.currentTarget;
    const width = img.naturalWidth;
    const height = img.naturalHeight;

    // initial crop width as % to guarantee at least minDimension in pixels
    const cropWidthPct = Math.min(100, Math.max((minDimension / width) * 100, 1));

    const base = makeAspectCrop(
      { unit: "%", width: cropWidthPct },
      aspect,
      width,
      height
    );

    const centered = centerCrop(base, width, height);
    setCrop(centered);
  };

  const doCrop = async () => {
    const img = imgRef.current;
    const canvas = previewCanvasRef.current;
    if (!img || !canvas || !crop) return;

    // IMPORTANT: use natural dimensions for accurate pixel crop
    const pixelCrop = convertToPixelCrop(
      crop,
      img.naturalWidth,
      img.naturalHeight
    );

    try {
      // Draw onto canvas (DPR-aware)
      setCanvasPreview(img, canvas, pixelCrop /*, { backgroundColor: "#fff" }*/);
      // Export
      const dataUrl = canvas.toDataURL("image/png");
      const blob = await canvasToBlob(canvas, "image/png", 0.92);
      updateAvatar?.(dataUrl, blob);
    } catch (e) {
      console.error("Crop failed:", e);
      const dataUrl = canvas.toDataURL("image/png");
      updateAvatar?.(dataUrl, null);
    }
  };

  // ---- UI ----
  const showFilePicker = !src; // only show internal picker when parent didn't supply src

  return (
    <div className={`text-center ${className}`}>
      {showFilePicker && (
        <label className="block mb-3 w-fit">
          <input type="file" accept={accept} onChange={onSelectFile} />
        </label>
      )}

      {error && <p className="text-red-400 text-xs">{error}</p>}

      {effectiveSrc && (
        <>
          <div className="text-center">
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              circularCrop
              keepSelection
              aspect={aspect}
              minWidth={minDimension}
            >
              <img
                ref={imgRef}
                src={effectiveSrc}
                alt="Upload"
                style={{ maxHeight: "70vh" }}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          </div>

          <Row className="text-center mx-auto pt-2">
            <Button
              outline
              size="sm"
              color="info"
              className="mx-auto"
              onClick={doCrop}
              disabled={!crop}
            >
              Crop Image
            </Button>
          </Row>

          {crop && (
            <canvas
              ref={previewCanvasRef}
              className="mt-4"
              style={{
                // keep visible so users see the circular result area size;
                // hide with display: "none" if you prefer
                border: "1px solid black",
                objectFit: "contain",
                width: 150,
                height: 150,
              }}
            />
          )}
        </>
      )}

      {!effectiveSrc && !showFilePicker && (
        <p className="text-xs text-zinc-500">No image selected.</p>
      )}
    </div>
  );
};

export default CircularImageCropper;